import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { Send } from 'lucide-react';
import Button from '@/components/primitives/button';
import Textarea from '@/components/primitives/textarea';
import { useAuth } from '@/context/auth-context';
import styles from './styles.module.scss';
import CaptionInput from '@/screen/feed/write/components/caption-input';

const COMMENT_MAX_LENGTH = 500;
// 한 줄 입력 높이가 글자마다 흔들리지 않도록 글자 수는 한도에 가까워질 때만 보인다.
const COMMENT_COUNTER_FROM = 400;

interface Props {
  newComment: string;
  isCommentSubmitting: boolean;
  onChangeComment: (text: string) => void;
  onSubmitComment: () => void;
  /**
   * sheet: 모바일 댓글 시트의 라벨·글자 수·등록 버튼 폼.
   * panel: PC 상세 댓글 패널의 한 줄 입력 + 보내기(lone-web-pc pc-v2-a-detail).
   */
  variant?: 'sheet' | 'panel';
}

export interface CommentFormRef {
  clear: () => void;
}

/**
 * Component for comment submission form
 */
const CommentForm = forwardRef<CommentFormRef, Props>((props, ref) => {
  const {
    newComment,
    isCommentSubmitting,
    onChangeComment,
    onSubmitComment,
    variant = 'sheet',
  } = props;
  const auth = useAuth();
  const textInputRef = useRef<HTMLInputElement>(null);
  const canWrite = !!auth.me?.activeAccount;
  const placeholder = canWrite ? '댓글로 의견을 남겨보세요' : '댓글을 쓰려면 로그인을 하셔야 해요';
  const submitDisabled = !newComment.trim() || isCommentSubmitting;

  // Expose clear function through ref
  useImperativeHandle(ref, () => ({
    clear: () => {
      if (textInputRef.current) {
        textInputRef.current.value = '';
      }
    },
  }), []);

  if (variant === 'panel') {
    const count = newComment.length;

    return (
      <form
        className={styles.panelForm}
        aria-busy={isCommentSubmitting}
        onSubmit={(e) => {
          e.preventDefault();
          if (!submitDisabled) onSubmitComment();
        }}
      >
        <div className={styles.panelInput}>
          <Textarea
            aria-label="새로운 댓글 쓰기"
            maxLength={COMMENT_MAX_LENGTH}
            placeholder={placeholder}
            value={newComment}
            onChange={(e) => onChangeComment?.call(null, e.target.value)}
            onKeyDown={(e) => {
              // Enter는 등록, Shift+Enter는 줄바꿈. 한글 조합 중 Enter는 조합 확정이라 넘긴다.
              if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
              e.preventDefault();
              if (!submitDisabled) onSubmitComment();
            }}
            readOnly={!canWrite}
            aria-disabled={!canWrite}
          />
          {count >= COMMENT_COUNTER_FROM ? (
            <span className={styles.panelCounter}>
              {count} / {COMMENT_MAX_LENGTH}
            </span>
          ) : null}
        </div>
        <button
          type="submit"
          className={styles.panelSubmit}
          aria-label="댓글 등록"
          disabled={submitDisabled}
        >
          <Send width={24} height={24} strokeWidth={1.5} aria-hidden="true" />
        </button>
      </form>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <CaptionInput
          label={'새로운 댓글 쓰기'}
          caption={newComment}
          onChange={(e) => onChangeComment?.call(null, e.target.value)}
          readOnly={!canWrite}
          aria-disabled={!canWrite}
          placeholder={placeholder}
        />
        <div className={styles.buttonContainer}>
          <Button
            aurora={isCommentSubmitting}
            onClick={onSubmitComment}
            disabled={submitDisabled}
          >
            등록
          </Button>
        </div>
      </div>
    </div>
  );
});

CommentForm.displayName = 'CommentForm';

export default CommentForm;
